import { useCallback } from 'react';
import { useVideoWorkspaceStore } from '@/stores/videoWorkspaceStore';

interface SceneNavigationOptions {
  slides: Array<{ id: string; childConversationId?: string | null }>;
  currentSlideId: string | null;
  isVideoMode: boolean;
  isCompositionMode: boolean;
  compositionRef: { current: { goToSlidePreview: (slideId: string) => void } | null };
  setCurrentSlideId: (slideId: string) => void;
  setActiveChatContext: (conversationId: string) => void;
  setActiveConversation: (conversationId: string) => void;
}

// Every way of picking a scene (filmstrip, Previous/Next, Ctrl/Cmd+Arrow) does the same thing: the studio's scene, the
// manual-pick mark that keeps background sync from moving the user, the scene's chat, and the composition canvas.
export function useSceneNavigation({
  slides,
  currentSlideId,
  isVideoMode,
  isCompositionMode,
  compositionRef,
  setCurrentSlideId,
  setActiveChatContext,
  setActiveConversation,
}: SceneNavigationOptions) {
  const currentIndex = slides.findIndex((s) => s.id === currentSlideId);

  const selectScene = useCallback((id: string) => {
    useVideoWorkspaceStore.getState().markManualSlideSelect();
    setCurrentSlideId(id);
    if (isVideoMode) {
      const conversationId = slides.find((s) => s.id === id)?.childConversationId;
      if (conversationId) {
        setActiveChatContext(conversationId);
        setActiveConversation(conversationId);
      }
    }
    if (isCompositionMode && compositionRef.current) {
      useVideoWorkspaceStore.getState().setCanvasTarget(id);
      compositionRef.current.goToSlidePreview(id);
    }
  }, [slides, isVideoMode, isCompositionMode, compositionRef, setCurrentSlideId, setActiveChatContext, setActiveConversation]);

  const prevScene = useCallback(() => {
    if (currentIndex > 0) selectScene(slides[currentIndex - 1].id);
  }, [currentIndex, slides, selectScene]);

  const nextScene = useCallback(() => {
    if (currentIndex < slides.length - 1) selectScene(slides[currentIndex + 1].id);
  }, [currentIndex, slides, selectScene]);

  return { selectScene, prevScene, nextScene };
}
